
import path from 'path';
import fs from 'fs';
import { tap } from 'ramda';
import { sh, log, tmp } from '@bb-cli/base';
import extractPackage from './extract-package';
import { zipDir } from './zip';
import { cxp5Statics, cxp6Statics } from './cxp-statics';

const EXTRACT_DIR = 'extract';
const PACK_DIR = 'pack';
const DEFAULT_CONTEXT = '[BBHOST]';
const LOG_PREFIX = 'PACK';
const PORTAL_VERSION_5 = '5';
const PORTAL_VERSION_6 = '6';

const logDirsCreated = ({ extractTo, packTo }) =>
  log.verbose(LOG_PREFIX, `Temp dirs: ${extractTo}, ${packTo}`);

const createWorkDirs = () =>
  tmp.dir()
  .then(({ dir }) => {
    // 1. extract provision package
    const extractTo = path.join(dir, EXTRACT_DIR);
    const packTo = path.join(dir, PACK_DIR);
    return { extractTo, packTo };
  });

const mv = (source, dest) => {
  sh.mkdir('-p', path.dirname(dest));
  fs.renameSync(source, dest);
};

const moveFiles = (mvOperations, toDir) =>
  Object.keys(mvOperations).map(mvFrom => mv(mvFrom, path.join(toDir, mvOperations[mvFrom])));

const pack = (itemsToPack, portalVersion, context, packTo, outFileP) => {
  const outFile = path.resolve(outFileP);
  const filesToPack = (portalVersion === PORTAL_VERSION_6)
    ? cxp6Statics(itemsToPack, context)
    : cxp5Statics(itemsToPack, context);
  log.silly(LOG_PREFIX, 'Mapping file paths', filesToPack);
  const result = {
    filename: outFile,
    files: Object.keys(filesToPack).map(k => filesToPack[k]),
  };
  log.verbose(LOG_PREFIX, `Create cxp file structure in ${packTo}`);
  moveFiles(filesToPack, packTo);
  log.verbose(LOG_PREFIX, `Zipping to ${outFile}`);
  return zipDir(packTo, outFile)
    .then(() => result);
};

const extractAndPack = (packageFile, outFile, portalVersion, context, extractTo, packTo) =>
  extractPackage(packageFile, extractTo)
    .then(itemsToPack => pack(itemsToPack, portalVersion, context, packTo, outFile));

/**
 * An object that describes a CXP statics package.
 * @typedef {Object} CxpStaticsPackage
 * @property {string} filename The filename of the package.
 * @property {Array.<string>} files The files in the package.
 */

/**
 * An object that describes a CXP statics directory.
 * @typedef {Object} CxpStaticsDir
 * @property {string} dir The directory.
 * @property {Array.<string>} files The files in the directory.
 */

/**
 * Turn a provision package into a cxp statics folder structure (zipped).
 *
 * For example, a provision package containing a widget and a feature will be turned
 * into something like:
 *
 * ```
 * features/[BBHOST]/my-feature/model.xml
 * features/[BBHOST]/my-feature/scripts/my-feature.js
 * widgets/[BBHOST]/my-widget/assets/icon.png
 * widgets/[BBHOST]/my-widget/index.html
 * widgets/[BBHOST]/my-widget/model.xml
 * widgets/[BBHOST]/my-widget/scripts/my-widget.js
 * ```
 *
 * @memberof module:@bb-cli/bb-package
 * @param {string} packageFile Path to provision package (zip of zips)
 * @param {string} outFile The name of the new zip file to create.
 * @param {object} options
 * @param {string} options.portalVersion The CXP version (5 or 6)
 * @param {string} options.context The CXP catalog context, if version 5 (default to '[BBHOST]')
 * @return {Promise.<CxpStaticsPackage>}
 * @example
 *
 * ```
 * packageCxpStatics('package.zip', 'cxp-statics.zip')
 *   .then(package => console.log(`Package created at ${package.filename}
 *                                    with ${package.files.length}`));
 * ```
 */
const packageCxpStatics = (packageFile, outFile, {
  portalVersion = PORTAL_VERSION_5,
  context = DEFAULT_CONTEXT } = {}
) =>
  createWorkDirs()
    .then(tap(logDirsCreated))
    .then(({ extractTo, packTo }) =>
      extractAndPack(packageFile, outFile, portalVersion, context, extractTo, packTo));


/**
 * Turn a provision package into a cxp statics structured folder.
 *
 * For example, a provision package containing a widget and a feature will be turned
 * into something like:
 *
 * ```
 * features/[BBHOST]/my-feature/model.xml
 * features/[BBHOST]/my-feature/scripts/my-feature.js
 * widgets/[BBHOST]/my-widget/assets/icon.png
 * widgets/[BBHOST]/my-widget/index.html
 * widgets/[BBHOST]/my-widget/model.xml
 * widgets/[BBHOST]/my-widget/scripts/my-widget.js
 * ```
 *
 * @memberof module:@bb-cli/bb-package
 * @param {string} packageFile Path to provision package (zip of zips)
 * @param {string} outDir The name of the new directory to create.
 * @param {object} options
 * @param {string} options.context The CXP catalog context (default to '[BBHOST]')
 * @return {Promise.<CxpStaticsDir>}
 * @example
 *
 * ```
 * packageCxpStaticsDir('package.zip', 'cxp-statics')
 *   .then(package => console.log(`Directory created at ${package.filename}
 *                                    with ${package.files.length}`));
 * ```
 */
const packageCxpStaticsDir = (packageFile, outDir, {
  portalVersion = PORTAL_VERSION_5,
  context = DEFAULT_CONTEXT,
} = {}) =>
  tmp.dir()
    .then(({ dir }) => extractPackage(packageFile, dir))
    .then(extractedItems => {
      const dir = path.resolve(outDir);
      const mvOperations = (portalVersion === PORTAL_VERSION_6)
        ? cxp6Statics(extractedItems)
        : cxp5Statics(extractedItems, context);
      const result = {
        dir,
        files: Object.keys(mvOperations).map(k => mvOperations[k]),
      };
      moveFiles(mvOperations, dir);
      return result;
    });

export default packageCxpStatics;
export { packageCxpStaticsDir };
